import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import LoadingRenderer from '../../../components/LoadingRenderer';
import OrgSelect, { NO_ORG } from '../../../components/OrgSelect/OrgSelect';
import { PageHeader } from '../../../components/PageHeader';
import Strings from '../../../Strings';
import { DEFAULT_TIMEZONE } from '../../../constants';
import actions from './redux/actions';

import './MessagesOverview.scss';
import './SuperuserHealthCenter.scss';

const RANGE_PRESETS = [
  { value: 'last_1h', label: 'Hourly' },
  { value: 'last_24h', label: '24 hours' },
  { value: 'last_7d', label: '7 days' },
  { value: 'last_28d', label: '28 days' },
];

function getPresetDurationMs(preset) {
  switch (preset) {
    case 'last_1h':
      return 60 * 60 * 1000;
    case 'last_24h':
      return 24 * 60 * 60 * 1000;
    case 'last_7d':
      return 7 * 24 * 60 * 60 * 1000;
    case 'last_28d':
    case 'last_30d':
      return 28 * 24 * 60 * 60 * 1000;
    default:
      return 24 * 60 * 60 * 1000;
  }
}

function formatRangeLabel(startAt, endAt) {
  const sameDay = startAt.toDateString() === endAt.toDateString();
  const dateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
  const dateTimeFmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  if (sameDay) {
    return `${dateTimeFmt.format(startAt)} - ${new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(endAt)}`;
  }

  return `${dateFmt.format(startAt)} - ${dateTimeFmt.format(endAt)}`;
}

function formatShortRangeLabel(startAt, endAt) {
  const sameYear = startAt.getFullYear() === endAt.getFullYear();
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });

  if (startAt.toDateString() === endAt.toDateString()) {
    return dateFormatter.format(startAt);
  }

  return `${dateFormatter.format(startAt)} - ${dateFormatter.format(endAt)}`;
}

function getTopDevicesChartHeight(itemCount) {
  return Math.max(360, (itemCount * 30) + 80);
}

function getFailCodesChartHeight(itemCount) {
  return Math.max(420, (itemCount * 42) + 100);
}

function FailCodeAnalysisTab({ getFailCodeAnalysis, onOpenEventDetails, orgIds }) {
  const [rangePreset, setRangePreset] = React.useState('last_24h');
  const [rangeEndAt, setRangeEndAt] = React.useState(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [analysis, setAnalysis] = React.useState(null);

  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE, []);
  const rangeDurationMs = React.useMemo(() => getPresetDurationMs(rangePreset), [rangePreset]);
  const effectiveEndAtMs = React.useMemo(() => (rangeEndAt === null ? Date.now() : rangeEndAt), [rangeEndAt]);
  const effectiveStartAtMs = effectiveEndAtMs - rangeDurationMs;
  const isAtLatestRange = rangeEndAt === null;
  const rangeLabel = React.useMemo(
    () => formatRangeLabel(new Date(effectiveStartAtMs), new Date(effectiveEndAtMs)),
    [effectiveEndAtMs, effectiveStartAtMs],
  );

  const requestParams = React.useMemo(() => {
    const params = rangeEndAt === null
      ? { timezone, range_mode: 'presets', preset: rangePreset, top: 20 }
      : {
          timezone,
          range_mode: 'custom',
          start_at: new Date(effectiveStartAtMs).toISOString(),
          end_at: new Date(effectiveEndAtMs).toISOString(),
          top: 20,
        };

    if (orgIds && orgIds.length > 0 && !orgIds.includes(NO_ORG)) {
      params.org_uuid = orgIds;
    }

    return params;
  }, [effectiveEndAtMs, effectiveStartAtMs, orgIds, rangeEndAt, rangePreset, timezone]);

  React.useEffect(() => {
    setIsLoading(true);
    setError('');

    getFailCodeAnalysis(requestParams)
      .then(resp => {
        setAnalysis(resp?.response || null);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Unable to load FAIL code analysis.');
        setIsLoading(false);
      });
  }, [getFailCodeAnalysis, refreshNonce, requestParams]);

  const moveRangeLeft = React.useCallback(() => {
    setRangeEndAt(prev => (prev ?? Date.now()) - rangeDurationMs);
  }, [rangeDurationMs]);

  const moveRangeRight = React.useCallback(() => {
    setRangeEndAt(prev => {
      if (prev === null) {
        return null;
      }
      const candidate = prev + rangeDurationMs;
      if (candidate >= Date.now()) {
        return null;
      }
      return candidate;
    });
  }, [rangeDurationMs]);

  const onPresetChange = React.useCallback(event => {
    setRangePreset(event.target.value);
    setRangeEndAt(null);
  }, []);

  const onRefresh = React.useCallback(() => {
    setRefreshNonce(prev => prev + 1);
  }, []);

  const topDevices = analysis?.top_devices_current || [];
  const failCodes = analysis?.fail_codes || [];
  const topDevicesChartHeight = React.useMemo(() => getTopDevicesChartHeight(topDevices.length), [topDevices.length]);
  const failCodesChartHeight = React.useMemo(() => getFailCodesChartHeight(failCodes.length), [failCodes.length]);
  const topDeviceItems = React.useMemo(
    () => topDevices.map(item => ({
      label: item.device_id || '-',
      count: item.count || 0,
      device_id: item.device_id,
    })),
    [topDevices],
  );
  const currentFailCodeItems = React.useMemo(
    () => failCodes.map(item => ({
      label: item.fail_code || '-',
      count: item.current || 0,
      fail_code: item.fail_code,
    })),
    [failCodes],
  );
  const previousFailCodeItems = React.useMemo(
    () => failCodes.map(item => ({
      label: item.fail_code || '-',
      count: item.previous || 0,
      fail_code: item.fail_code,
    })),
    [failCodes],
  );
  const chartRangeLabels = React.useMemo(() => {
    const currentStart = analysis?.filters?.start_at ? new Date(analysis.filters.start_at) : new Date(effectiveStartAtMs);
    const currentEnd = analysis?.filters?.end_at ? new Date(analysis.filters.end_at) : new Date(effectiveEndAtMs);
    const previousStart = analysis?.filters?.previous_start_at ? new Date(analysis.filters.previous_start_at) : new Date(effectiveStartAtMs - rangeDurationMs);
    const previousEnd = analysis?.filters?.previous_end_at ? new Date(analysis.filters.previous_end_at) : new Date(effectiveStartAtMs);

    return {
      current: formatShortRangeLabel(currentStart, currentEnd),
      previous: formatShortRangeLabel(previousStart, previousEnd),
    };
  }, [
    analysis?.filters?.end_at,
    analysis?.filters?.previous_end_at,
    analysis?.filters?.previous_start_at,
    analysis?.filters?.start_at,
    effectiveEndAtMs,
    effectiveStartAtMs,
    rangeDurationMs,
  ]);

  const openCurrentPeriodDetails = React.useCallback(extraFilters => {
    if (!analysis?.filters?.start_at || !analysis?.filters?.end_at) {
      return;
    }

    onOpenEventDetails({
      start_at: analysis.filters.start_at,
      end_at: analysis.filters.end_at,
      ...extraFilters,
    });
  }, [analysis?.filters?.end_at, analysis?.filters?.start_at, onOpenEventDetails]);

  const openPreviousPeriodDetails = React.useCallback(extraFilters => {
    if (!analysis?.filters?.previous_start_at || !analysis?.filters?.previous_end_at) {
      return;
    }

    onOpenEventDetails({
      start_at: analysis.filters.previous_start_at,
      end_at: analysis.filters.previous_end_at,
      ...extraFilters,
    });
  }, [analysis?.filters?.previous_end_at, analysis?.filters?.previous_start_at, onOpenEventDetails]);

  const onTopDeviceClick = React.useCallback(label => {
    const item = topDeviceItems.find(entry => entry.label === label);
    openCurrentPeriodDetails({
      hw_codes: ['FAIL'],
      source_devices: item?.device_id ? [item.device_id] : [],
    });
  }, [openCurrentPeriodDetails, topDeviceItems]);

  const onCurrentFailCodeClick = React.useCallback(label => {
    const item = currentFailCodeItems.find(entry => entry.label === label);
    openCurrentPeriodDetails({
      hw_codes: ['FAIL'],
      str1_values: item?.fail_code ? [item.fail_code] : [],
      str1_match_mode: 'exact',
    });
  }, [currentFailCodeItems, openCurrentPeriodDetails]);

  const onPreviousFailCodeClick = React.useCallback(label => {
    const item = previousFailCodeItems.find(entry => entry.label === label);
    openPreviousPeriodDetails({
      hw_codes: ['FAIL'],
      str1_values: item?.fail_code ? [item.fail_code] : [],
      str1_match_mode: 'exact',
    });
  }, [openPreviousPeriodDetails, previousFailCodeItems]);

  return (
    <div className="health-messages">
      <PageHeader
        growRight
        left="FAIL Code Analysis"
        right={(
          <div className="health-messages__header-controls">
            <div className="health-messages__range-nav health-messages__range-nav--centered">
              <button type="button" className="health-messages__range-nav-button" onClick={moveRangeLeft}>
                &larr;
              </button>
              <div className="health-messages__range-label">{rangeLabel}</div>
              <button
                type="button"
                className="health-messages__range-nav-button"
                onClick={moveRangeRight}
                disabled={isAtLatestRange}
              >
                &rarr;
              </button>
            </div>
            <div className="health-messages__header-actions">
              <select
                className="health-messages__range-select"
                value={rangePreset}
                onChange={onPresetChange}
              >
                {RANGE_PRESETS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <OrgSelect />
              <button type="button" className="health-messages__refresh" onClick={onRefresh}>
                {Strings.refresh || 'Refresh'}
              </button>
            </div>
          </div>
        )}
      />
      <div className="health-messages__subtitle health-messages__subtitle--page">
        FAIL events grouped by reason, with current vs previous period comparison.
      </div>

      <LoadingRenderer loading={isLoading}>
        <>
          {error ? <div className="health-messages-error">{error}</div> : null}

          {!error && analysis ? (
            <>
              <div className="health-messages-grid health-messages-grid--breakdowns">
                <div className="health-messages-panel">
                  <div className="health-messages-panel__header">
                    <div className="health-messages-panel__title">
                      Top 20 devices with FAIL events in
                      {' '}
                      {chartRangeLabels.current}
                    </div>
                  </div>
                  <div className="health-messages-panel__body is-chart" style={{ height: topDevicesChartHeight + 28 }}>
                    <GenericCategoryGraph
                      graphData={topDeviceItems}
                      graphType={chartTypeEnum.horizontalBar}
                      dataSeries={[{ name: 'FAIL events', color: '#205375', selector: item => item.count }]}
                      categorySelector={item => item.label}
                      tooltipTitle="FAIL events"
                      yAxisWidth={240}
                      onCategoryClicked={onTopDeviceClick}
                    />
                  </div>
                </div>
              </div>

              <div className="health-messages-grid health-messages-grid--breakdowns">
                <div className="health-messages-panel">
                  <div className="health-messages-panel__header">
                    <div className="health-messages-panel__title">
                      FAIL events grouped by reason:
                      {' '}
                      {chartRangeLabels.current}
                    </div>
                  </div>
                  <div className="health-messages-panel__body is-chart" style={{ height: failCodesChartHeight + 28 }}>
                    <GenericCategoryGraph
                      graphData={currentFailCodeItems}
                      graphType={chartTypeEnum.horizontalBar}
                      dataSeries={[{ name: chartRangeLabels.current, color: '#c56b3d', selector: item => item.count }]}
                      categorySelector={item => item.label}
                      tooltipTitle="FAIL events"
                      yAxisWidth={300}
                      onCategoryClicked={onCurrentFailCodeClick}
                    />
                  </div>
                </div>

                <div className="health-messages-panel">
                  <div className="health-messages-panel__header">
                    <div className="health-messages-panel__title">
                      FAIL events grouped by reason:
                      {' '}
                      {chartRangeLabels.previous}
                    </div>
                  </div>
                  <div className="health-messages-panel__body is-chart" style={{ height: failCodesChartHeight + 28 }}>
                    <GenericCategoryGraph
                      graphData={previousFailCodeItems}
                      graphType={chartTypeEnum.horizontalBar}
                      dataSeries={[{ name: chartRangeLabels.previous, color: '#8fa3b8', selector: item => item.count }]}
                      categorySelector={item => item.label}
                      tooltipTitle="FAIL events"
                      yAxisWidth={300}
                      onCategoryClicked={onPreviousFailCodeClick}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </>
      </LoadingRenderer>
    </div>
  );
}

FailCodeAnalysisTab.propTypes = {
  getFailCodeAnalysis: PropTypes.func.isRequired,
  onOpenEventDetails: PropTypes.func.isRequired,
  orgIds: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = state => ({
  orgIds: state.entities?.orgFilter?.orgIds,
});

const mapDispatchToProps = dispatch => ({
  getFailCodeAnalysis: params => dispatch(actions.getFailCodeAnalysis(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FailCodeAnalysisTab);
